// ✅ src/pages/VehicleV2/VehicleDailyProgressEntrySLT.jsx
// Design aligned with JobCardEntryDetail / DailyProgressListing.
//
// GET  /api/dailyProgressEntrySLT/vehicle-header
// GET  /api/dailyProgressEntrySLT/init
// POST /api/dailyProgressEntrySLT/save
// GET  /api/dailyProgressEntrySLT/search
// POST /api/dailyProgressEntrySLT/delete

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaSearch, FaTrash, FaUndo, FaArrowLeft } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/dailyProgressEntrySLT";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toText = (v, d = "") => {
  if (v === null || v === undefined) return d;
  return String(v);
};

const clean = (v) => String(v ?? "").trim();

const pad2 = (n) => String(n).padStart(2, "0");

const todayDDMMYYYY = () => {
  const dt = new Date();
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const parseDDMMYYYY = (s) => {
  const parts = String(s || "").split("/");
  if (parts.length !== 3) return null;
  const d = Number(parts[0]);
  const m = Number(parts[1]);
  const y = Number(parts[2]);
  if (!Number.isFinite(d) || !Number.isFinite(m) || !Number.isFinite(y)) return null;
  const dt = new Date(y, m - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== m - 1 || dt.getDate() !== d) {
    return null;
  }
  return dt;
};

const isValidDDMMYYYY = (s) => !!parseDDMMYYYY(s);

const parseAnyDateToDDMMYYYY = (raw) => {
  const val = clean(raw);
  if (!val) return "";
  if (isValidDDMMYYYY(val)) return val;
  const dt = new Date(val);
  if (Number.isNaN(dt.getTime())) return "";
  return `${pad2(dt.getDate())}/${pad2(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};

const VehicleDailyProgressEntrySLT = () => {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const routerLocation = useLocation();
  const params = useParams();

  const getLS = (key, fallback = "") => {
    try {
      const val = localStorage.getItem(key);
      return val !== null && val !== undefined ? val : fallback;
    } catch {
      return fallback;
    }
  };

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid") || "0";
  const branchType = getLS("branchType") || getLS("branchtype") || "HO";
  const userId = getLS("userId") || getLS("loginuserid") || getLS("loginUserId");
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") ||
    userId ||
    "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const vehicleId = useMemo(() => {
    const sp = new URLSearchParams(routerLocation?.search || "");
    return (
      toInt(routerLocation?.state?.vehicleId, 0) ||
      toInt(routerLocation?.state?.id, 0) ||
      toInt(params?.vehicleId, 0) ||
      toInt(params?.id, 0) ||
      toInt(sp.get("vehicleId"), 0) ||
      toInt(sp.get("vehicleid"), 0) ||
      toInt(sp.get("id"), 0) ||
      toInt(getLS("vehicleId"), 0) ||
      0
    );
  }, [routerLocation, params]);

  const progressIdFromSource = useMemo(() => {
    const sp = new URLSearchParams(routerLocation?.search || "");
    return (
      toInt(routerLocation?.state?.progressId, 0) ||
      toInt(params?.progressId, 0) ||
      toInt(sp.get("progressId"), 0) ||
      0
    );
  }, [routerLocation, params]);

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);
  const [deletingId, setDeletingId] = useState(0);

  const isBusy = loading || pageLoading || saving;

  const [vehicleInfo, setVehicleInfo] = useState({
    vehicleId: vehicleId || 0,
    vehicleCode: "",
    vehicleRegNo: "",
    branchName: "",
    vehicleType: "",
    vehicleSubtype: "",
    vehicleBasis: "",
  });

  const [locations, setLocations] = useState([]);
  const [months, setMonths] = useState([
    { id: "01", name: "January" },
    { id: "02", name: "February" },
    { id: "03", name: "March" },
    { id: "04", name: "April" },
    { id: "05", name: "May" },
    { id: "06", name: "June" },
    { id: "07", name: "July" },
    { id: "08", name: "August" },
    { id: "09", name: "September" },
    { id: "10", name: "October" },
    { id: "11", name: "November" },
    { id: "12", name: "December" },
  ]);
  const [years, setYears] = useState(() => {
    const y = new Date().getFullYear();
    return Array.from({ length: 8 }).map((_, i) => ({
      id: String(y - 4 + i),
      name: String(y - 4 + i),
    }));
  });

  const [form, setForm] = useState({
    date: todayDDMMYYYY(),
    locationId: "",
    reasonForIdle: "",
    remarks: "",
    startingKm: "",
    startingHours: "",
    usageManPowerCost: "",
    driverName: "",
    fuelTankCapacity: "",
    fuelFilled: "",
    fuelConsumption: "",
    status: "Running",
    natureOfWork: "",
    noOfTonnesM3: "",
    closingKm: "",
    closingHours: "",
    productionQty: "",
    noOfTrips: "",
    noOfLocations: "",
    openingFuel: "",
    totalFuelInTank: "",
    closingFuelInTank: "",
    volume: "0",
    progressId: progressIdFromSource || 0,
  });

  const [filter, setFilter] = useState({
    fromDate: "",
    toDate: "",
    month: "",
    year: "",
    locationId: "",
  });

  const [list, setList] = useState([]);

  const inputClass = `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block mb-2 text-sm font-medium ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const readonlyBox = `w-full px-4 py-2 rounded-lg border font-medium ${
    isDark
      ? "bg-gray-700/40 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const cellClass = `px-6 py-4 text-sm ${
    isDark ? "text-gray-300" : "text-gray-900"
  }`;

  const applyFuelMath = (next) => {
    const opening = Number(next.openingFuel) || 0;
    const filled = Number(next.fuelFilled) || 0;
    const cons = Number(next.fuelConsumption) || 0;
    next.totalFuelInTank = String(opening + filled);
    next.closingFuelInTank = String(opening + filled - cons);
    return next;
  };

  const setField = useCallback((key, val) => {
    setForm((p) => {
      const next = { ...p, [key]: val };
      if (key === "status" && (val === "Idle" || val === "Breakdown")) {
        next.closingKm = next.startingKm;
        next.closingHours = next.startingHours;
      }
      if (
        key === "openingFuel" ||
        key === "fuelFilled" ||
        key === "fuelConsumption"
      ) {
        applyFuelMath(next);
      }
      if (key === "startingKm" && (next.status === "Idle" || next.status === "Breakdown")) {
        next.closingKm = val;
      }
      if (key === "startingHours" && (next.status === "Idle" || next.status === "Breakdown")) {
        next.closingHours = val;
      }
      return next;
    });
  }, []);

  const setFilterField = useCallback((key, val) => {
    setFilter((p) => ({ ...p, [key]: val }));
  }, []);

  const mapVehicleHeader = useCallback(
    (raw) => {
      const r = raw || {};
      return {
        vehicleId: toInt(r?.vehicleId ?? r?.fld_vehicleid ?? vehicleId ?? 0, vehicleId || 0),
        vehicleCode: toText(r?.vehicleCode ?? r?.fld_vehicle_code),
        vehicleRegNo: toText(r?.vehicleRegNo ?? r?.fld_vehicle_regno),
        branchName: toText(r?.branchName ?? r?.fld_branchname),
        vehicleType: toText(r?.vehicleType ?? r?.fld_vehicletypename),
        vehicleSubtype: toText(r?.vehicleSubtype ?? r?.fld_vehicle_subtypename),
        vehicleBasis: toText(r?.vehicleBasis ?? r?.fld_vehicle_basistype),
      };
    },
    [vehicleId]
  );

  const mapLocation = useCallback((l) => {
    const raw = l || {};
    return {
      id: toText(raw?.id ?? raw?.locationId ?? raw?.fld_Worklocationid ?? raw?.value),
      name: toText(
        raw?.name ?? raw?.locationName ?? raw?.fld_Worklocationname ?? raw?.text
      ),
      _raw: raw,
    };
  }, []);

  const mapEntry = useCallback((e) => {
    const raw = e || {};
    return {
      date: parseAnyDateToDDMMYYYY(raw?.date),
      locationId: toText(raw?.locationId ?? raw?.fld_Worklocationid),
      status: toText(raw?.status, "Running"),
      reasonForIdle: toText(raw?.reasonForIdle),
      natureOfWork: toText(raw?.natureOfWork),
      remarks: toText(raw?.remarks),
      startingKm: toText(raw?.startingKm),
      closingKm: toText(raw?.closingKm),
      startingHours: toText(raw?.startingHours),
      closingHours: toText(raw?.closingHours),
      usageManPowerCost: toText(raw?.usageManPowerCost),
      productionQty: toText(raw?.productionQty),
      noOfTrips: toText(raw?.noOfTrips),
      noOfLocations: toText(raw?.noOfLocations),
      noOfTonnesM3: toText(raw?.noOfTonnesM3),
      driverName: toText(raw?.driverName),
      fuelTankCapacity: toText(raw?.fuelTankCapacity),
      fuelFilled: toText(raw?.fuelFilled),
      fuelConsumption: toText(raw?.fuelConsumption),
      openingFuel: toText(raw?.openingFuel),
      totalFuelInTank: toText(raw?.totalFuelInTank),
      closingFuelInTank: toText(raw?.closingFuelInTank),
      volume: toText(raw?.volume ?? raw?.fld_volume, "0"),
      progressId: toInt(raw?.progressId, 0),
    };
  }, []);

  const mapSearchRow = useCallback((r) => {
    const raw = r || {};
    return {
      progressId: toInt(raw?.progressId ?? raw?.id ?? raw?.fld_progressid, 0),
      date: toText(raw?.date ?? raw?.fld_date),
      locationName: toText(
        raw?.locationName ?? raw?.location ?? raw?.fld_Worklocationname
      ),
      status: toText(raw?.status),
      startingKm: toText(raw?.startingKm),
      closingKm: toText(raw?.closingKm),
      startingHours: toText(raw?.startingHours),
      closingHours: toText(raw?.closingHours),
      noOfTonnesM3: toText(raw?.noOfTonnesM3),
      productionQty: toText(raw?.productionQty),
      noOfTrips: toText(raw?.noOfTrips),
      fuelFilled: toText(raw?.fuelFilled),
      _raw: raw,
    };
  }, []);

  const fetchVehicleHeader = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/vehicle-header`, {
        params: {
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleId, 0),
          progressId: toInt(progressIdFromSource, 0),
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Vehicle header load failed");
      setVehicleInfo(mapVehicleHeader(data?.vehicleInfo || data?.header || data));
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Vehicle header load failed");
    } finally {
      setLoading(false);
    }
  }, [branchId, vehicleId, progressIdFromSource, commonHeaders, mapVehicleHeader]);

  const fetchInit = useCallback(async () => {
    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleId, 0),
          progressId: toInt(progressIdFromSource, 0),
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setLocations(Array.isArray(data?.locations) ? data.locations.map(mapLocation) : []);

      if (Array.isArray(data?.months) && data.months.length) {
        setMonths(
          data.months.map((m) => ({
            id: toText(m?.id ?? m?.value ?? m?.monthId),
            name: toText(m?.name ?? m?.text ?? m?.monthName),
          }))
        );
      }
      if (Array.isArray(data?.years) && data.years.length) {
        setYears(
          data.years.map((y) => ({
            id: toText(y?.id ?? y?.value ?? y?.yearId ?? y?.name),
            name: toText(y?.name ?? y?.text ?? y?.yearName ?? y?.id),
          }))
        );
      }
      if (data?.entry) {
        const entry = mapEntry(data.entry);
        setForm((p) => ({
          ...p,
          ...entry,
          date: clean(entry.date) || p.date || todayDDMMYYYY(),
          locationId: clean(entry.locationId) || p.locationId,
          status: clean(entry.status) || "Running",
          volume: clean(entry.volume) || "0",
          progressId: toInt(entry.progressId, 0) || toInt(p.progressId, 0) || 0,
        }));
      } else if (data?.progressId) {
        setForm((p) => ({ ...p, progressId: toInt(data.progressId, p.progressId) }));
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Init failed");
    } finally {
      setPageLoading(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleId,
    progressIdFromSource,
    commonHeaders,
    mapLocation,
    mapEntry,
  ]);

  const validate = useCallback(() => {
    if (!clean(form.date)) return "Please select date...";
    if (!isValidDDMMYYYY(form.date)) return "Date must be in dd/MM/yyyy format";
    const dt = parseDDMMYYYY(form.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dt && dt > today) return "Select date lesser than or equal to current date";
    if (!clean(form.locationId)) return "Please Select Location";
    if (!clean(form.status) || form.status === "Select") return "Please Select Status";
    if (!clean(form.fuelFilled)) return "Please Fill Fuel Qty";
    if (!clean(form.fuelConsumption)) return "Please enter Fuel Consumption";
    if (form.status === "Running" && !clean(form.productionQty)) {
      return "Please Fill production Qty";
    }
    if (form.status !== "Running" && !clean(form.remarks)) {
      return "Please Fill Remarks";
    }
    if (!clean(form.startingKm)) return "Starting KM is required";
    if (!clean(form.closingKm)) return "Closing KM is required";
    if (!clean(form.startingHours)) return "Starting Hours is required";
    if (!clean(form.closingHours)) return "Closing Hours is required";
    if ((Number(form.closingHours) || 0) - (Number(form.startingHours) || 0) > 24) {
      return "working hour should always less than 24 hours";
    }
    if ((Number(form.closingHours) || 0) < (Number(form.startingHours) || 0)) {
      return "Closing hour should always be greater than Starting hour";
    }
    if ((Number(form.closingKm) || 0) < (Number(form.startingKm) || 0)) {
      return "Closing km should always be greater than Starting km";
    }
    if (form.status === "Idle" || form.status === "Breakdown") {
      if (clean(form.startingKm) !== clean(form.closingKm)) {
        return "Starting and Closing kilometers should be same for idle or breakdown";
      }
      if (clean(form.startingHours) !== clean(form.closingHours)) {
        return "Starting and Closing hours should be same for idle or breakdown";
      }
    }
    if (!clean(form.usageManPowerCost)) return "Usage/Man Power Cost is required";
    const basis = clean(vehicleInfo.vehicleBasis).toUpperCase();
    if (basis === "TONNE" && !clean(form.noOfTonnesM3)) {
      return "Please enter no of tonnes";
    }
    if (!clean(form.noOfLocations)) return "No of Locations is required";
    if (!clean(form.openingFuel)) return "Opening Fuel is required";
    return "";
  }, [form, vehicleInfo.vehicleBasis]);

  const submitEntry = useCallback(async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        loginUserId: toInt(loginUserId, 0),
        branchId: toInt(branchId, 0),
        vehicleId: toInt(vehicleInfo.vehicleId || vehicleId, 0),
        progressId: toInt(form.progressId, 0),
        date: clean(form.date),
        locationId: clean(form.locationId),
        status: clean(form.status),
        reasonForIdle: clean(form.reasonForIdle),
        natureOfWork: clean(form.natureOfWork),
        remarks: clean(form.remarks),
        startingKm: clean(form.startingKm),
        closingKm: clean(form.closingKm),
        startingHours: clean(form.startingHours),
        closingHours: clean(form.closingHours),
        usageManPowerCost: clean(form.usageManPowerCost),
        driverName: clean(form.driverName),
        noOfTonnesM3: clean(form.noOfTonnesM3),
        productionQty: clean(form.productionQty),
        noOfTrips: clean(form.noOfTrips),
        noOfLocations: clean(form.noOfLocations),
        fuelTankCapacity: clean(form.fuelTankCapacity),
        fuelFilled: clean(form.fuelFilled),
        fuelConsumption: clean(form.fuelConsumption) || "0",
        openingFuel: clean(form.openingFuel) || "0",
        totalFuelInTank: clean(form.totalFuelInTank) || "0",
        closingFuelInTank: clean(form.closingFuelInTank) || "0",
        volume: clean(form.volume) || "0",
      };
      const { data } = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Save failed");
      const newId = toInt(data?.progressId, 0);
      if (newId) setForm((p) => ({ ...p, progressId: newId }));
      toast.success(data?.message || "Saved successfully");
      await searchEntries();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }, [validate, loginUserId, branchId, vehicleInfo.vehicleId, vehicleId, form, commonHeaders]);

  const resetForm = useCallback(() => {
    setForm((p) => ({
      ...p,
      date: todayDDMMYYYY(),
      locationId: "",
      reasonForIdle: "",
      remarks: "",
      startingKm: "",
      startingHours: "",
      usageManPowerCost: "",
      driverName: "",
      fuelTankCapacity: "",
      fuelFilled: "",
      fuelConsumption: "",
      status: "Running",
      natureOfWork: "",
      noOfTonnesM3: "",
      closingKm: "",
      closingHours: "",
      productionQty: "",
      noOfTrips: "",
      noOfLocations: "",
      openingFuel: "",
      totalFuelInTank: "",
      closingFuelInTank: "",
      volume: "0",
    }));
  }, []);

  const searchEntries = useCallback(async () => {
    try {
      setSearching(true);
      const { data } = await axios.get(`${API_BASE}/search`, {
        params: {
          loginUserId: toInt(loginUserId, 0),
          branchId: toInt(branchId, 0),
          vehicleId: toInt(vehicleInfo.vehicleId || vehicleId, 0),
          fromDate: clean(filter.fromDate),
          toDate: clean(filter.toDate),
          month: clean(filter.month),
          year: clean(filter.year),
          locationId: clean(filter.locationId),
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Search failed");
      setList(Array.isArray(data?.list) ? data.list.map(mapSearchRow) : []);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Search failed");
      setList([]);
    } finally {
      setSearching(false);
    }
  }, [
    loginUserId,
    branchId,
    vehicleInfo.vehicleId,
    vehicleId,
    filter,
    commonHeaders,
    mapSearchRow,
  ]);

  const deleteEntry = useCallback(
    async (delProgressId) => {
      const pid = toInt(delProgressId, 0);
      if (!pid) {
        toast.error("progressId missing");
        return;
      }
      try {
        setDeletingId(pid);
        const { data } = await axios.post(
          `${API_BASE}/delete`,
          {
            progressId: pid,
            loginUserId: toInt(loginUserId, 0),
            branchId: toInt(branchId, 0),
          },
          { headers: commonHeaders }
        );
        if (!data?.success) throw new Error(data?.message || "Delete failed");
        toast.success(data?.message || "Deleted successfully");
        await searchEntries();
      } catch (err) {
        console.error(err);
        toast.error(err?.response?.data?.message || err?.message || "Delete failed");
      } finally {
        setDeletingId(0);
      }
    },
    [loginUserId, branchId, commonHeaders, searchEntries]
  );

  useEffect(() => {
    if (!vehicleId) {
      toast.error("Vehicle ID missing. Open from listing again.");
      return;
    }
    if (!toInt(branchId, 0) || !toInt(loginUserId, 0)) {
      toast.error("Session missing (branchId/loginUserId). Please login again.");
      return;
    }
    fetchVehicleHeader();
    fetchInit();
  }, [branchId, loginUserId, vehicleId, progressIdFromSource, fetchVehicleHeader, fetchInit]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">VEHICLE DAILY PROGRESS ENTRY</span>
          }
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Daily Progress Entry
              </h2>
            </div>

            <div className="flex gap-2">
              <button
                onClick={resetForm}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${
                  isDark
                    ? "border-gray-600 text-gray-200 hover:bg-gray-700"
                    : "border-gray-300 text-gray-700 hover:bg-white"
                }`}
              >
                <FaUndo />
                Reset
              </button>
              <button
                onClick={submitEntry}
                disabled={isBusy}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass} ${
                  isBusy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {saving ? "Saving..." : "Submit"}
              </button>
            </div>
          </div>

          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Vehicle Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>Vehicle Code</label>
                <div className={readonlyBox}>{vehicleInfo.vehicleCode || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Reg No</label>
                <div className={readonlyBox}>{vehicleInfo.vehicleRegNo || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Branch Name</label>
                <div className={readonlyBox}>{vehicleInfo.branchName || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Type</label>
                <div className={readonlyBox}>{vehicleInfo.vehicleType || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Subtype</label>
                <div className={readonlyBox}>{vehicleInfo.vehicleSubtype || "-"}</div>
              </div>
              <div>
                <label className={labelClass}>Vehicle Basis</label>
                <div className={readonlyBox}>{vehicleInfo.vehicleBasis || "-"}</div>
              </div>
            </div>
          </div>

          <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
            Vehicle Position Entry
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4">
            <div className="space-y-4">
              <div>
                <label className={labelClass}>Date *</label>
                <input className={inputClass} value={form.date} onChange={(e) => setField("date", e.target.value)} placeholder="dd/MM/yyyy" disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Location *</label>
                <select className={inputClass} value={form.locationId} onChange={(e) => setField("locationId", e.target.value)} disabled={isBusy}>
                  <option value="">Select</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Reason For Idle</label>
                <textarea className={inputClass} rows={2} value={form.reasonForIdle} onChange={(e) => setField("reasonForIdle", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Remarks</label>
                <textarea className={inputClass} rows={2} value={form.remarks} onChange={(e) => setField("remarks", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Starting KM *</label>
                <input className={inputClass} value={form.startingKm} onChange={(e) => setField("startingKm", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Starting Hours *</label>
                <input className={inputClass} value={form.startingHours} onChange={(e) => setField("startingHours", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Usage / Man Power / Cost *</label>
                <input className={inputClass} value={form.usageManPowerCost} onChange={(e) => setField("usageManPowerCost", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Driver Name</label>
                <input className={inputClass} value={form.driverName} onChange={(e) => setField("driverName", e.target.value)} disabled={isBusy} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Fuel Tank Capacity</label>
                  <input className={inputClass} value={form.fuelTankCapacity} disabled />
                </div>
                <div>
                  <label className={labelClass}>Fuel Filled *</label>
                  <input className={inputClass} value={form.fuelFilled} onChange={(e) => setField("fuelFilled", e.target.value)} disabled={isBusy} />
                </div>
                <div>
                  <label className={labelClass}>Fuel Consumption *</label>
                  <input className={inputClass} value={form.fuelConsumption} onChange={(e) => setField("fuelConsumption", e.target.value)} disabled={isBusy} />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={labelClass}>Status *</label>
                <select className={inputClass} value={form.status} onChange={(e) => setField("status", e.target.value)} disabled={isBusy}>
                  <option value="Running">Running</option>
                  <option value="Idle">Idle</option>
                  <option value="Breakdown">Breakdown</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Nature Of Work</label>
                <textarea className={inputClass} rows={2} value={form.natureOfWork} onChange={(e) => setField("natureOfWork", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>No. of Tonnes / M3</label>
                <input className={inputClass} value={form.noOfTonnesM3} onChange={(e) => setField("noOfTonnesM3", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Closing KM *</label>
                <input className={inputClass} value={form.closingKm} onChange={(e) => setField("closingKm", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Closing Hours *</label>
                <input className={inputClass} value={form.closingHours} onChange={(e) => setField("closingHours", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>Production Qty *</label>
                <input className={inputClass} value={form.productionQty} onChange={(e) => setField("productionQty", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>No of Trips</label>
                <input className={inputClass} value={form.noOfTrips} onChange={(e) => setField("noOfTrips", e.target.value)} disabled={isBusy} />
              </div>
              <div>
                <label className={labelClass}>No of Locations *</label>
                <input className={inputClass} value={form.noOfLocations} onChange={(e) => setField("noOfLocations", e.target.value)} disabled={isBusy} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>Opening Fuel *</label>
                  <input className={inputClass} value={form.openingFuel} onChange={(e) => setField("openingFuel", e.target.value)} disabled={isBusy} />
                </div>
                <div>
                  <label className={labelClass}>Total Fuel in Tank *</label>
                  <input className={inputClass} value={form.totalFuelInTank} disabled />
                </div>
                <div>
                  <label className={labelClass}>Closing Fuel in Tank *</label>
                  <input className={inputClass} value={form.closingFuelInTank} disabled={isBusy} />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-blue-900"}`}>
              Filtration Area
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label className={labelClass}>From Date</label>
                <input className={inputClass} value={filter.fromDate} onChange={(e) => setFilterField("fromDate", e.target.value)} placeholder="dd/MM/yyyy" disabled={searching} />
              </div>
              <div>
                <label className={labelClass}>To Date</label>
                <input className={inputClass} value={filter.toDate} onChange={(e) => setFilterField("toDate", e.target.value)} placeholder="dd/MM/yyyy" disabled={searching} />
              </div>
              <div>
                <label className={labelClass}>Month</label>
                <select className={inputClass} value={filter.month} onChange={(e) => setFilterField("month", e.target.value)} disabled={searching}>
                  <option value="">Select</option>
                  {months.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Year</label>
                <select className={inputClass} value={filter.year} onChange={(e) => setFilterField("year", e.target.value)} disabled={searching}>
                  <option value="">Select</option>
                  {years.map((y) => (
                    <option key={y.id} value={y.id}>{y.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelClass}>Location</label>
                <select className={inputClass} value={filter.locationId} onChange={(e) => setFilterField("locationId", e.target.value)} disabled={searching}>
                  <option value="">Select</option>
                  {locations.map((l) => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={searchEntries}
                  disabled={searching}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass}`}
                >
                  <FaSearch />
                  {searching ? "Searching..." : "Search"}
                </button>
              </div>
            </div>
          </div>

          <h3 className={`text-lg font-semibold mb-3 ${isDark ? "text-white" : "text-gray-900"}`}>
            Progress Listing
          </h3>

          <div className="overflow-x-auto rounded-lg border shadow-lg">
            {searching ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Searching...</div>
              </div>
            ) : list.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
              </div>
            ) : (
              <table className={`w-full min-w-[1400px] ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    {["Date", "Location", "Status", "Start KM", "Close KM", "Start Hrs", "Close Hrs", "Tonnes/M3", "Prod Qty", "Trips", "Fuel Filled", "Action"].map((h) => (
                      <th key={h} className="px-6 py-4 text-left text-sm font-semibold">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {list.map((r, idx) => (
                    <tr
                      key={r.progressId || idx}
                      className={`${
                        idx % 2 === 0
                          ? isDark ? "bg-gray-800" : "bg-white"
                          : isDark ? "bg-gray-750" : "bg-gray-50"
                      } ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}
                    >
                      <td className={cellClass}>{r.date || "-"}</td>
                      <td className={cellClass}>{r.locationName || "-"}</td>
                      <td className="px-6 py-4 text-sm">
                        {r.status ? (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isDark ? "bg-yellow-900/40 text-yellow-200" : "bg-yellow-100 text-yellow-800"
                          }`}>
                            {r.status}
                          </span>
                        ) : "-"}
                      </td>
                      <td className={cellClass}>{r.startingKm || "-"}</td>
                      <td className={cellClass}>{r.closingKm || "-"}</td>
                      <td className={cellClass}>{r.startingHours || "-"}</td>
                      <td className={cellClass}>{r.closingHours || "-"}</td>
                      <td className={cellClass}>{r.noOfTonnesM3 || "-"}</td>
                      <td className={cellClass}>{r.productionQty || "-"}</td>
                      <td className={cellClass}>{r.noOfTrips || "-"}</td>
                      <td className={cellClass}>{r.fuelFilled || "-"}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => deleteEntry(r.progressId)}
                          disabled={deletingId === r.progressId}
                          className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700"
                        >
                          <FaTrash />
                          {deletingId === r.progressId ? "Deleting..." : "Delete"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleDailyProgressEntrySLT;